import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "api";
import styles from "../stripe-dashboard.module.css";

const fmtBRL = v =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v ?? 0);

function PaymentsHistoryTab({ users, plans, token }) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const [page, setPage] = useState(0);
  const limit = 50;

  useEffect(() => {
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users.slice(0, 50);
    return users
      .filter(u =>
        (u.username || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [users, userSearch]);

  const fetchHistory = async (opts = {}) => {
    if (!token || !selectedUserId) return;
    const offset = opts.offset ?? page * limit;

    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/v2/stripe/payments/history", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          user_id: selectedUserId,
          plan_id: selectedPlanId ? Number(selectedPlanId) : undefined,
          limit,
          offset,
        },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao carregar histórico de pagamentos.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const canSearch = !!token && !!selectedUserId && !loading;

  return (
    <div className={styles.card}>
      <p className={styles.cardTitle}>Histórico de Pagamentos</p>

      <div className={styles.formBody}>
        <div className={styles.formGrid2}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Cliente</label>
            <div ref={dropdownRef} style={{ position: "relative" }}>
              <input
                type="text"
                className={styles.formControl}
                placeholder="Digite para buscar por nome ou e-mail..."
                value={userSearch}
                onChange={e => {
                  setUserSearch(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                autoComplete="off"
              />
              {showDropdown && filteredUsers.length > 0 && (
                <div className={styles.dropdown}>
                  {filteredUsers.map(u => (
                    <div
                      key={u.id}
                      className={styles.dropdownItem}
                      onMouseDown={() => {
                        setSelectedUserId(u.id);
                        setUserSearch(u.username || u.email);
                        setShowDropdown(false);
                        setPage(0);
                        setData(null);
                      }}
                    >
                      {u.username}
                      <span style={{ color: "#94a3b8", fontSize: "0.8rem", marginLeft: 6 }}>
                        ({u.email})
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <span className={styles.formHint}>
              Selecione um cliente para buscar o histórico.
            </span>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Plano (opcional)</label>
            <select
              className={styles.formControl}
              value={selectedPlanId}
              onChange={e => {
                setSelectedPlanId(e.target.value);
                setPage(0);
                setData(null);
              }}
              disabled={!selectedUserId}
            >
              <option value="">Todos os planos</option>
              {plans.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} — R$ {Number(p.price ?? 0).toFixed(2)}/mês
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={!canSearch}
            onClick={() => fetchHistory({ offset: 0 })}
          >
            {loading ? (
              <>
                <div className={styles.spinner} style={{ width: 14, height: 14, borderWidth: 2 }} />
                Buscando...
              </>
            ) : (
              <>
                <i className="fas fa-search" /> Buscar
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 14, color: "#dc2626", fontWeight: 600 }}>
          {error}
        </div>
      )}

      {data && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 800, color: "#0f172a" }}>
              {data.user?.username || "Cliente"}{" "}
              <span style={{ color: "#64748b", fontWeight: 700 }}>
                · {data.user?.email}
              </span>
            </div>
            <div style={{ color: "#64748b", fontWeight: 700 }}>
              {data.totals?.count ?? 0} pagamentos · total {fmtBRL(data.totals?.sum_amount ?? 0)}
            </div>
          </div>

          <div className={styles.tableWrap} style={{ marginTop: 10 }}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Plano</th>
                  <th>Status</th>
                  <th>Valor</th>
                  <th>ID</th>
                </tr>
              </thead>
              <tbody>
                {(data.items || []).map(it => (
                  <tr key={it.id}>
                    <td className={styles.mono}>
                      {it.payment_date ? new Date(it.payment_date).toLocaleString("pt-BR") : "—"}
                    </td>
                    <td>{it.plan_name || `Plano #${it.plan_id}`}</td>
                    <td>
                      <span className={`${styles.badge} ${it.status === "paid" ? styles.badgeGreen : styles.badgeBlue}`}>
                        {it.status || "—"}
                      </span>
                    </td>
                    <td className={styles.mono}>{fmtBRL(it.amount)}</td>
                    <td className={styles.mono}>{it.payment_id || it.event_id || `#${it.id}`}</td>
                  </tr>
                ))}
                {(data.items || []).length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", color: "#94a3b8", padding: "2rem", fontStyle: "italic" }}>
                      Nenhum pagamento encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
            <button
              type="button"
              className={`${styles.button}`}
              disabled={loading || page === 0}
              onClick={() => {
                const next = Math.max(0, page - 1);
                setPage(next);
                fetchHistory({ offset: next * limit });
              }}
            >
              <i className="fas fa-chevron-left" /> Anterior
            </button>
            <button
              type="button"
              className={`${styles.button}`}
              disabled={loading || (data.items || []).length < limit}
              onClick={() => {
                const next = page + 1;
                setPage(next);
                fetchHistory({ offset: next * limit });
              }}
            >
              Próxima <i className="fas fa-chevron-right" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { PaymentsHistoryTab };

